import React, { useCallback, useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import '../HospitalDashboard.css';
import {
  AddPatientMonitor,
  AddVentilator,
  fetchBedMachines,
  UpdateBedDevice,
} from '../../../helper/DeviceHelper';
import AddMachineModal from './AddMachineModal';
import { ACTIVE_DEVICE } from '../../../common/constant/common-constants';
import moment from 'moment';

const displayValue = (value) => (value == null || value === '' ? '—' : value);

const formatDateTime = (value) => {
  if (!value) return '—';
  return moment(value).format('DD-MM-YYYY HH:mm');
};

const SuccessToast = ({ message }) => (
  <div className="hospital-toast" role="status" aria-live="polite">
    <span className="hospital-toast-icon" aria-hidden="true">✓</span>
    <div className="hospital-toast-text">
      <strong>{message}</strong>
    </div>
  </div>
);

const ErrorToast = ({ message }) => (
  <div className="hospital-toast hospital-toast--error" role="alert">
    <span className="hospital-toast-icon" aria-hidden="true">!</span>
    <div className="hospital-toast-text">
      <strong>{message}</strong>
    </div>
  </div>
);

const MachineCard = ({ machine, typeLabel }) => (
  <article className="hospital-card hospital-card--static">
    <div className="hospital-card-head">
      <h3 className="hospital-card-name">{typeLabel}</h3>
      <span className={`hospital-status-badge ${machine.isActive ? 'hospital-status-badge--active' : 'hospital-status-badge--inactive'}`}>
        {machine.isActive ? 'Active' : 'Inactive'}
      </span>
    </div>

    <div className="hospital-card-details">
      <div className="hospital-card-detail-row">
        <span className="hospital-card-detail-label">ID</span>
        <span className="hospital-card-detail-val">{displayValue(machine.id)}</span>
      </div>
      {machine.ventilatorId != null && (
        <div className="hospital-card-detail-row">
          <span className="hospital-card-detail-label">DEVICE</span>
          <span className="hospital-card-detail-val">{displayValue(machine.ventilatorId)}</span>
        </div>
      )}
      {machine.NoccarcPMSerialNumberData != null && (
        <div className="hospital-card-detail-row">
          <span className="hospital-card-detail-label">SERIAL</span>
          <span className="hospital-card-detail-val">{displayValue(machine.NoccarcPMSerialNumberData)}</span>
        </div>
      )}
      <div className="hospital-card-detail-row">
        <span className="hospital-card-detail-label">CREATED</span>
        <span className="hospital-card-detail-val">{formatDateTime(machine.createdAt)}</span>
      </div>
      <div className="hospital-card-detail-row">
        <span className="hospital-card-detail-label">UPDATED</span>
        <span className="hospital-card-detail-val">{formatDateTime(machine.updatedAt)}</span>
      </div>
    </div>
  </article>
);

const MachinesDashboard = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const queryParams = new URLSearchParams(location.search);
  const bedId = queryParams.get('bedId');

  const [bed, setBed] = useState({});
  const [ward, setWard] = useState({});
  const [hospital, setHospital] = useState({});
  const [patientMonitor, setPatientMonitor] = useState([]);
  const [ventilator, setVentilator] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [showSuccessToast, setShowSuccessToast] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');

  const fetchMachinesData = useCallback(() => {
    if (!bedId) return;
    fetchBedMachines(bedId)
      .then((res) => {
        setBed(res.bed || {});
        setWard(res.ward || {});
        setHospital(res.hospital || {});
        setPatientMonitor(res.patientMonitor ? [res.patientMonitor] : []);
        setVentilator(res.ventilator ? [res.ventilator] : []);
      })
      .catch((err) => console.error('Error fetching devices:', err));
  }, [bedId]);

  useEffect(() => {
    fetchMachinesData();
  }, [fetchMachinesData]);

  const showSuccess = (message) => {
    setSuccessMessage(message);
    setShowSuccessToast(true);
    setErrorMessage('');
    setTimeout(() => setShowSuccessToast(false), 3000);
  };

  const showError = (message) => {
    setErrorMessage(message);
    setTimeout(() => setErrorMessage(''), 4000);
  };

  const handleAddMachine = (newMachine) => {
    if (newMachine.machineType === 'ventilator') {
      AddVentilator(newMachine)
        .then((addedVentilator) => {
          const ventilatorId = addedVentilator?.ventilator?.id;
          showSuccess('Ventilator added successfully');
          setIsModalOpen(false);
          return UpdateBedDevice(bedId, null, ventilatorId);
        })
        .then(() => fetchMachinesData())
        .catch((error) => {
          console.error('Error adding ventilator:', error);
          showError('Failed to add ventilator.');
        });
      return;
    }

    if (newMachine.machineType === 'patientMonitor') {
      AddPatientMonitor(newMachine)
        .then((addedPatientMonitor) => {
          const monitorId = addedPatientMonitor?.patientMonitor?.id;
          showSuccess('Patient monitor added successfully');
          setIsModalOpen(false);
          return UpdateBedDevice(bedId, monitorId, null);
        })
        .then(() => fetchMachinesData())
        .catch((error) => {
          console.error('Error adding patient monitor:', error);
          showError('Failed to add patient monitor.');
        });
    }
  };

  const totalMachines = ventilator.length + patientMonitor.length;
  const bedLabel = bed?.name || bedId;

  return (
    <div className="hospital-page">
      {showSuccessToast && <SuccessToast message={successMessage} />}
      {errorMessage && <ErrorToast message={errorMessage} />}

      <nav className="hospital-breadcrumb" aria-label="Breadcrumb">
        <button type="button" onClick={() => navigate(`/hospital-details/wards?hospitalId=${hospital?.id}`)}>
          {displayValue(hospital?.name)}
        </button>
        <span className="hospital-breadcrumb-sep">›</span>
        <button type="button" onClick={() => navigate(`/hospital-details/beds?wardId=${ward?.id}`)}>
          {displayValue(ward?.name)}
        </button>
        <span className="hospital-breadcrumb-sep">›</span>
        <span>{displayValue(bedLabel)}</span>
        <span className="hospital-breadcrumb-sep">›</span>
        <span>Machines</span>
      </nav>

      <div className="hospital-header">
        <h3 className="hospital-title">Machines</h3>
      </div>

      <div className="hospital-filters-bar">
        <button
          type="button"
          className="hospital-btn hospital-btn--primary"
          onClick={() => setIsModalOpen(true)}
        >
          Add Machine
        </button>
      </div>

      <div className="hospital-stats-grid">
        <div className="hospital-stat-card hospital-stat-card--total">
          <span className="hospital-stat-label">TOTAL MACHINES</span>
          <span className="hospital-stat-value">{totalMachines}</span>
        </div>
      </div>

      <div className="hospital-toolbar">
        <h2 className="hospital-section-title">{ACTIVE_DEVICE.V730i}</h2>
      </div>

      <div className="hospital-cards-scroll">
        {ventilator.length === 0 ? (
          <div className="hospital-empty">No ventilators assigned to this bed.</div>
        ) : (
          <div className="hospital-cards-grid">
            {ventilator.map((machine) => (
              <MachineCard key={machine.id} machine={machine} typeLabel="Ventilator" />
            ))}
          </div>
        )}
      </div>

      {patientMonitor.length > 0 && (
        <>
          <div className="hospital-toolbar">
            <h2 className="hospital-section-title">Patient Monitor</h2>
          </div>
          <div className="hospital-cards-scroll">
            <div className="hospital-cards-grid">
              {patientMonitor.map((machine) => (
                <MachineCard key={machine.id} machine={machine} typeLabel="Patient Monitor" />
              ))}
            </div>
          </div>
        </>
      )}

      {isModalOpen && (
        <AddMachineModal
          onClose={() => setIsModalOpen(false)}
          onSave={handleAddMachine}
          bedId={bedId}
        />
      )}
    </div>
  );
};

export default MachinesDashboard;
